import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';

import { CommonCodeDTO } from '@/apis/repositories/types/common';
import { fetchCommonCode } from '@/apis/services/commonService';
import { CommonCodeBitWrapper, CommonCodeListWrapper, CommonCodeWrapper } from '@/modules/commonCode';
import { CommonCodeItem } from '@/modules/commonCode/@type';
import { rootStore } from '@/store';

@Module({
  name: 'commonCode',
  namespaced: true,
  dynamic: true,
  store: rootStore,
})
export class CommonCode extends VuexModule {
  _state = {
    loaded: false,
    list: {} as Record<string, CommonCodeListWrapper>,
    bit: {} as Record<string, CommonCodeBitWrapper>,
  };

  get isLoaded() {
    return this._state.loaded;
  }

  get listWrapper() {
    return (codeKey: string) => {
      return this._state.list[codeKey] || new CommonCodeListWrapper('unknown', [], '');
    };
  }

  get list() {
    return (codeKey: string) => {
      const wrapper = this._state.list[codeKey] || new CommonCodeListWrapper('unknown', [], '');

      return wrapper.getList();
    };
  }

  get codeList() {
    return (codeKey: string) => {
      const wrapper = this._state.list[codeKey] || new CommonCodeListWrapper('unknown', [], '');

      return wrapper.getList()?.map((codeMap) => codeMap.code);
    };
  }

  get listItem() {
    return (codeKey: string, code: string) => {
      const wrapper = this._state.list[codeKey] || new CommonCodeListWrapper('unknown', [], '');

      return wrapper.getItem(code);
    };
  }

  get listPickItems() {
    return (codeKey: string, codes: string[]) => {
      const wrapper = this._state.list[codeKey] || new CommonCodeListWrapper('unknown', [], '');

      return wrapper.pickItems(codes);
    };
  }

  get bitWrapper() {
    return (codeKey: string) => {
      return this._state.bit[codeKey] || new CommonCodeBitWrapper('unknown', [], '');
    };
  }

  get bit() {
    return (codeKey: string) => {
      const wrapper = this._state.bit[codeKey] || new CommonCodeBitWrapper('unknown', [], '');

      return wrapper.getList();
    };
  }

  get bitItem() {
    return (codeKey: string, code: number) => {
      const wrapper = this._state.bit[codeKey] || new CommonCodeBitWrapper('unknown', [], '');

      return wrapper.getItem(code);
    };
  }

  get bitPickItems() {
    return (codeKey: string, codes: number[]) => {
      const wrapper = this._state.bit[codeKey] || new CommonCodeBitWrapper('unknown', [], '');

      return wrapper.pickItems(codes);
    };
  }

  get bitAndBitwise() {
    return (codeKey: string, code: number) => {
      const wrapper = this._state.bit[codeKey] || new CommonCodeBitWrapper('unknown', [], '');

      return wrapper.getAndBitwise(code);
    };
  }

  @Mutation
  setCommonCode(data: CommonCodeDTO) {
    Object.keys(data.bit).forEach((codeKey) => {
      this._state.bit[codeKey] = new CommonCodeBitWrapper(
        codeKey,
        data.bit[codeKey].metaDataItemList.map((item) => {
          return {
            code: item.code,
            text: item.value,
            description: item.description,
            extraData: item.extraData,
          } as CommonCodeItem<number>;
        }),
        data.bit[codeKey].description,
      );
    });
    Object.keys(data.list).forEach((codeKey) => {
      this._state.list[codeKey] = new CommonCodeListWrapper(
        codeKey,
        data.list[codeKey].metaDataItemList.map((item) => {
          return {
            code: item.code,
            text: item.value,
            description: item.description,
            extraData: item.extraData,
          } as CommonCodeItem<string>;
        }),
        data.list[codeKey].description,
      );
    });

    this._state.loaded = true;
  }

  @Action
  async fetchCommonCode() {
    const response = await fetchCommonCode();
    this.setCommonCode(response);
    CommonCodeWrapper.validateCommonBit(response.bit);

    return response;
  }
}

export const commonCodeStore = getModule(CommonCode);
